import React, { useState, useEffect } from "react";
import Layout from "../Component/Layout";
import axios from "axios";
import Select from "react-select";
import { hasPermission } from "../utils/permission";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MASTER_API, PURCHASE_API } from "../apiConfig";

const th = {
    padding: "12px 14px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: 700,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.4px"
};

const td = {
    padding: "12px 14px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "middle"
};

const btn = {
    padding: "9px 16px",
    borderRadius: "8px",
    border: "none",
    background: "#031b4e",
    color: "#fff",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer"
};

const input = {
    width: "100%",
    padding: "9px 12px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box"
};

const label = {
    display: "block",
    fontSize: "12px",
    fontWeight: 600,
    color: "#334155",
    marginBottom: "6px"
};

const iconBtn = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "16px",
    padding: "4px 6px",
    borderRadius: "6px"
};

const viewBtn = {
    padding: "7px 18px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#1e293b",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer"
};

const deleteIconBtn = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: "6px 8px",
    borderRadius: "8px",
    color: "#dc2626",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center"
};

const selectStyles = {
    control: (base) => ({
        ...base,
        minHeight: "38px",
        borderRadius: "8px",
        borderColor: "#cbd5e1",
        fontSize: "13px"
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px" }),
    indicatorsContainer: (base) => ({ ...base, height: "36px" }),
    menu: (base) => ({ ...base, fontSize: "13px", zIndex: 20 }),
    menuPortal: (base) => ({ ...base, zIndex: 2000 })
};

const showToast = (msg, type = "success") => {
    toast[type](msg, { position: "top-right", autoClose: 2500 });
};

const DEFAULT_ROW_COUNT = 5;

const makeEmptyRows = (count) =>
    Array.from({ length: count }, (_, i) => ({
        sl_no: i + 1,
        parameter_name: "",
        verification_parameter: "",
        uom: "",
        specified_value: ""
    }));

const LabCalibrationStandard = () => {
    const canView = hasPermission("LAB_CALIBRATION_STANDARD", "view");
    const canCreate = hasPermission("LAB_CALIBRATION_STANDARD", "create");
    const canEdit = hasPermission("LAB_CALIBRATION_STANDARD", "edit");
    const canDelete = hasPermission("LAB_CALIBRATION_STANDARD", "delete");

    const [rows, setRows] = useState([]);

    // Standards dropdown — sourced from Configuration (calibration-standard master)
    const [standardOptions, setStandardOptions] = useState([]);
    const [selectedStandard, setSelectedStandard] = useState(null);

    // UOM dropdown — sourced from Configuration (uom master)
    const [uomOptions, setUomOptions] = useState([]);

    // Modal state. mode: "add" | "view" | "edit"
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState("add");
    const [editingId, setEditingId] = useState(null);
    const [modalStandard, setModalStandard] = useState(null); // {value, label}
    const [paramRows, setParamRows] = useState(makeEmptyRows(DEFAULT_ROW_COUNT));

    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    // ================= LOAD =================
    const fetchRows = async () => {
        try {
            const res = await axios.get(`${PURCHASE_API}/lab-calibration-standard`);
            setRows(res.data || []);
        } catch (err) {
            console.error(err);
            showToast("Failed to load Lab Calibration Standard list", "error");
        }
    };

    const fetchStandardOptions = async () => {
        try {
            const res = await axios.get(
                `${MASTER_API}/configuration/dynamic/calibration-standard`
            );
            setStandardOptions(
                (res.data || []).map((s) => ({ value: s.id, label: s.name }))
            );
        } catch (err) {
            console.error(err);
        }
    };

    const fetchUomOptions = async () => {
        try {
            const res = await axios.get(`${MASTER_API}/configuration/uom`);
            setUomOptions(
                (res.data || []).map((u) => ({ value: u.id, label: u.name }))
            );
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchRows();
        fetchStandardOptions();
        fetchUomOptions();
        // eslint-disable-next-line
    }, []);

    // ================= ADD FLOW =================
    const openAddModal = () => {
        if (!canCreate) {
            showToast("You don't have Create access. Please contact admin.", "error");
            return;
        }
        if (!selectedStandard) return;
        setModalMode("add");
        setModalStandard(selectedStandard);
        setEditingId(null);
        setParamRows(makeEmptyRows(DEFAULT_ROW_COUNT));
        setShowModal(true);
    };

    // ================= VIEW / EDIT FLOW =================
    const openViewOrEdit = async (row, mode) => {
        if (mode === "edit" && !canEdit) {
            showToast("You don't have Edit access. Please contact admin.", "error");
            return;
        }
        try {
            const res = await axios.get(`${PURCHASE_API}/lab-calibration-standard/${row.standard_id}`);
            const data = res.data;
            const existing = (data.parameters || []).map((p) => ({
                sl_no: p.sl_no,
                parameter_name: p.parameter_name,
                verification_parameter: p.verification_parameter || "",
                uom: p.uom || "",
                specified_value: p.specified_value || ""
            }));
            const padded =
                existing.length >= DEFAULT_ROW_COUNT
                    ? existing
                    : [...existing, ...makeEmptyRows(DEFAULT_ROW_COUNT - existing.length).map((r, i) => ({ ...r, sl_no: existing.length + i + 1 }))];

            setModalMode(mode);
            setModalStandard({ value: data.standard_id, label: data.standard_name });
            setEditingId(data.standard_id);
            setParamRows(mode === "view" ? existing : padded);
            setShowModal(true);
        } catch (err) {
            console.error(err);
            showToast("Failed to load record", "error");
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setModalStandard(null);
        setEditingId(null);
        setParamRows(makeEmptyRows(DEFAULT_ROW_COUNT));
    };

    // Switches an open View modal into Edit mode in place, without closing it
    const switchToEditMode = () => {
        setModalMode("edit");
        setParamRows((rows) => {
            if (rows.length >= DEFAULT_ROW_COUNT) return rows;
            const extra = makeEmptyRows(DEFAULT_ROW_COUNT - rows.length).map((r, i) => ({
                ...r,
                sl_no: rows.length + i + 1
            }));
            return [...rows, ...extra];
        });
    };

    // ================= PARAMETER ROW HANDLING =================
    const addParamRow = () => {
        setParamRows((rows) => [
            ...rows,
            {
                sl_no: rows.length + 1,
                parameter_name: "",
                verification_parameter: "",
                uom: "",
                specified_value: ""
            }
        ]);
    };

    const updateParamRow = (index, field, value) => {
        setParamRows((rows) =>
            rows.map((r, i) => (i === index ? { ...r, [field]: value } : r))
        );
    };

    const removeParamRow = (index) => {
        setParamRows((rows) =>
            rows.filter((_, i) => i !== index).map((r, i) => ({ ...r, sl_no: i + 1 }))
        );
    };

    // ================= SAVE (Add or Edit) =================
    const handleSave = async () => {
        if (!modalStandard) {
            showToast("Standard is required", "error");
            return;
        }
        const parameters = paramRows
            .filter((r) => r.parameter_name.trim())
            .map((r) => ({
                parameter_name: r.parameter_name.trim(),
                verification_parameter: (r.verification_parameter || "").trim(),
                uom: (r.uom || "").trim(),
                specified_value: (r.specified_value || "").trim()
            }));
        if (!parameters.length) {
            showToast("Add at least one parameter", "error");
            return;
        }

        const payload = {
            standard_id: modalStandard.value,
            standard_name: modalStandard.label,
            parameters
        };

        try {
            if (modalMode === "edit" && editingId) {
                // editingId here is standard_id — all rows for that standard get replaced
                await axios.put(`${PURCHASE_API}/lab-calibration-standard/${editingId}`, payload);
                showToast("Updated successfully");
            } else {
                await axios.post(`${PURCHASE_API}/lab-calibration-standard`, payload);
                showToast("Added successfully");
                setSelectedStandard(null);
            }
            closeModal();
            fetchRows();
        } catch (err) {
            console.error(err);
            showToast(err?.response?.data?.error || "Save failed", "error");
        }
    };

    // ================= DELETE =================
    const handleDelete = async (id) => {
        if (!canDelete) {
            showToast("You don't have Delete access. Please contact admin.", "error");
            setConfirmDeleteId(null);
            return;
        }
        try {
            await axios.delete(`${PURCHASE_API}/lab-calibration-standard/${id}`);
            showToast("Deleted successfully");
            setConfirmDeleteId(null);
            fetchRows();
        } catch (err) {
            console.error(err);
            showToast("Delete failed", "error");
        }
    };

    if (!canView) {
        return (
            <Layout>
                <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
                    You don't have access to view this page.
                </div>
            </Layout>
        );
    }

    const readOnly = modalMode === "view";

    return (
        <Layout>
            <ToastContainer />
            <div style={{ padding: "24px" }}>
                <h2 style={{ margin: 0 }}>Lab Calibration Standard</h2>
                <p style={{ color: "#64748b", marginTop: "6px" }}>
                    Maintain calibration records for lab instruments and equipment.
                </p>

                {/* ================= STANDARD PICKER ================= */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "flex-end",
                        gap: "12px",
                        margin: "20px 0",
                        maxWidth: "460px"
                    }}
                >
                    <div style={{ flex: 1 }}>
                        <label style={label}>Standard</label>
                        <Select
                            options={standardOptions}
                            value={selectedStandard}
                            onChange={(opt) => setSelectedStandard(opt)}
                            placeholder="Select a standard..."
                            isClearable
                        />
                    </div>
                    {selectedStandard && (
                        <button style={btn} onClick={openAddModal}>
                            + Add
                        </button>
                    )}
                </div>

                {/* ================= LIST ================= */}
                <div
                    style={{
                        background: "#fff",
                        borderRadius: "14px",
                        border: "1px solid #e2e8f0",
                        overflow: "hidden"
                    }}
                >
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead style={{ background: "#f8fafc" }}>
                            <tr>
                                <th style={th}>Standard</th>
                                <th style={th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td style={td} colSpan={2}>
                                        No records found.
                                    </td>
                                </tr>
                            )}
                            {rows.map((row) => (
                                <tr key={row.standard_id}>
                                    <td style={td}>{row.standard_name}</td>
                                    <td style={td}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                            <button
                                                style={viewBtn}
                                                onClick={() => openViewOrEdit(row, "view")}
                                            >
                                                View
                                            </button>
                                            <button
                                                title="Delete"
                                                style={deleteIconBtn}
                                                onClick={() => setConfirmDeleteId(row.standard_id)}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                                    <path d="M10 11v6"></path>
                                                    <path d="M14 11v6"></path>
                                                    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ================= ADD / VIEW / EDIT MODAL ================= */}
            {showModal && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(15,23,42,0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000
                    }}
                >
                    <div
                        style={{
                            background: "#fff",
                            borderRadius: "14px",
                            width: "920px",
                            maxWidth: "95vw",
                            maxHeight: "85vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden"
                        }}
                    >
                        {/* ---- FIXED HEADER ---- */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "24px 24px 16px",
                                borderBottom: "1px solid #f1f5f9",
                                flexShrink: 0
                            }}
                        >
                            <div>
                                <h3 style={{ margin: 0 }}>
                                    {modalMode === "view"
                                        ? "View Parameters"
                                        : modalMode === "edit"
                                        ? "Edit Parameters"
                                        : "Add Parameters"}
                                </h3>
                                <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b" }}>
                                    {modalStandard?.label}
                                </p>
                            </div>
                            {!readOnly && (
                                <button
                                    style={{ ...btn, padding: "7px 12px", fontSize: "12px" }}
                                    onClick={addParamRow}
                                >
                                    + Add Row
                                </button>
                            )}
                        </div>

                        {/* ---- SCROLLABLE PARAMETER ROWS ---- */}
                        <div style={{ overflowY: "auto", overflowX: "auto", padding: "16px 24px", flex: 1 }}>
                            <table style={{ width: "100%", minWidth: "760px", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...th, width: "70px" }}>SL NO</th>
                                        <th style={th}>Parameter</th>
                                        <th style={th}>Verification Parameter</th>
                                        <th style={th}>UOM</th>
                                        <th style={th}>Specified Value</th>
                                        {!readOnly && <th style={{ ...th, width: "40px" }}></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paramRows.map((row, idx) => (
                                        <tr key={idx}>
                                            <td style={td}>{row.sl_no}</td>
                                            <td style={td}>
                                                {readOnly ? (
                                                    row.parameter_name
                                                ) : (
                                                    <input
                                                        style={input}
                                                        value={row.parameter_name}
                                                        onChange={(e) => updateParamRow(idx, "parameter_name", e.target.value)}
                                                        placeholder={`Parameter ${row.sl_no}`}
                                                    />
                                                )}
                                            </td>
                                            <td style={td}>
                                                {readOnly ? (
                                                    row.verification_parameter
                                                ) : (
                                                    <input
                                                        style={input}
                                                        value={row.verification_parameter}
                                                        onChange={(e) => updateParamRow(idx, "verification_parameter", e.target.value)}
                                                        placeholder="Verification Parameter"
                                                    />
                                                )}
                                            </td>
                                            <td style={{ ...td, minWidth: "160px" }}>
                                                {readOnly ? (
                                                    row.uom
                                                ) : (
                                                    <Select
                                                        styles={selectStyles}
                                                        options={uomOptions}
                                                        value={
                                                            uomOptions.find((o) => o.label === row.uom) || null
                                                        }
                                                        onChange={(opt) =>
                                                            updateParamRow(idx, "uom", opt ? opt.label : "")
                                                        }
                                                        placeholder="Select UOM..."
                                                        isClearable
                                                        menuPortalTarget={document.body}
                                                    />
                                                )}
                                            </td>
                                            <td style={td}>
                                                {readOnly ? (
                                                    row.specified_value
                                                ) : (
                                                    <input
                                                        style={input}
                                                        value={row.specified_value}
                                                        onChange={(e) => updateParamRow(idx, "specified_value", e.target.value)}
                                                        placeholder="Specified Value"
                                                    />
                                                )}
                                            </td>
                                            {!readOnly && (
                                                <td style={td}>
                                                    <button
                                                        title="Remove row"
                                                        style={iconBtn}
                                                        onClick={() => removeParamRow(idx)}
                                                    >
                                                        ✖️
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* ---- FIXED FOOTER ---- */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "10px",
                                padding: "16px 24px 24px",
                                borderTop: "1px solid #f1f5f9",
                                flexShrink: 0
                            }}
                        >
                            <button
                                style={{ ...btn, background: "#fff", color: "#334155", border: "1px solid #cbd5e1" }}
                                onClick={closeModal}
                            >
                                {readOnly ? "Close" : "Cancel"}
                            </button>
                            {readOnly && canEdit && (
                                <button style={btn} onClick={switchToEditMode}>
                                    Edit
                                </button>
                            )}
                            {!readOnly && (
                                <button style={btn} onClick={handleSave}>
                                    Save
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ================= DELETE CONFIRM ================= */}
            {confirmDeleteId && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(15,23,42,0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000
                    }}
                >
                    <div style={{ background: "#fff", borderRadius: "14px", padding: "24px", width: "360px" }}>
                        <p style={{ marginTop: 0 }}>Are you sure you want to delete this record?</p>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                            <button
                                style={{ ...btn, background: "#fff", color: "#334155", border: "1px solid #cbd5e1" }}
                                onClick={() => setConfirmDeleteId(null)}
                            >
                                Cancel
                            </button>
                            <button
                                style={{ ...btn, background: "#dc2626" }}
                                onClick={() => handleDelete(confirmDeleteId)}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default LabCalibrationStandard;